import type { DataSourceInfo } from '../types/api'
import {
  getProviderIcon,
  getProviderName,
  getFieldUnit,
  isIntegerField,
} from '../lib/constants'
import { useFieldLabels } from '../hooks/queries'
import { formatDataValue } from '../lib/utils'

interface DataSourceSectionProps {
  dataSource?: DataSourceInfo
  data: Record<string, unknown>
  title?: string
  /** If true, show null values as "k.A." - used for dynamic provider data. Default: false (filter out nulls) */
  showNullValues?: boolean
}

export default function DataSourceSection({ dataSource, data, title, showNullValues = false }: DataSourceSectionProps) {
  const icon = dataSource ? getProviderIcon(dataSource.provider) : '📊'
  const displayName = dataSource ? getProviderName(dataSource.provider) : 'Aktuelle Daten'

  // Fetch field labels for this provider
  const { getDisplayLabel, isFieldVisible } = useFieldLabels(dataSource?.provider)

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  // Check if field is a rank field (should show "value / total")
  const isRankField = (key: string): boolean => {
    return key.endsWith('_rank')
  }

  const formatValue = (key: string, value: unknown): string => {
    const formatted = formatDataValue(key, value, { isIntegerField })

    // For rank fields, append " / total" from records_imported
    if (isRankField(key) && typeof value === 'number' && dataSource?.records_imported) {
      return `${formatted} / ${dataSource.records_imported}`
    }

    return formatted
  }

  return (
    <div className="card-gut8 overflow-hidden">
      {/* Header Row - Provider, Dataset, Edition, Badge */}
      <div className="flex items-center gap-3 px-6 py-4 bg-gut8-bg-muted border-b border-border-gut8-light -mx-6 -mt-6 mb-4">
        <span className="text-2xl">{icon}</span>
        <div className="flex-1 flex items-center gap-3">
          <span className="font-semibold text-gut8-anthracite">{title || displayName}</span>
          {dataSource && (
            <>
              <span className="text-text-muted">·</span>
              <span className="text-sm text-text-muted">Edition {dataSource.edition}</span>
            </>
          )}
        </div>
        {dataSource?.is_latest && (
          <span className="px-3 py-1.5 bg-primary-subtle text-primary rounded-full text-xs font-medium">
            Aktuell
          </span>
        )}
        {!dataSource && (
          <span className="px-3 py-1.5 bg-primary-subtle text-primary rounded-full text-xs font-medium">
            Aktuell
          </span>
        )}
      </div>

      {/* Info Row - Jahresausgabe, Importiert, Quelldatei - only if dataSource exists */}
      {dataSource && (
        <div className="px-6 py-3 bg-gut8-bg-muted border-b border-border-gut8-light -mx-6 mb-4">
          <div className="flex flex-wrap items-center gap-8 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-muted">Jahresausgabe:</span>
              <span className="font-semibold text-gut8-anthracite">{dataSource.reference_date_display}</span>
            </div>

            <div className="flex items-center gap-2">
              <span className="font-medium text-text-muted">Importiert:</span>
              <span className="text-gut8-anthracite">{formatDate(dataSource.import_date)}</span>
            </div>

            {dataSource.import_file && (
              <div className="flex items-center gap-2">
                <span className="font-medium text-text-muted">Quelldatei:</span>
                <span className="text-xs font-mono text-text-muted bg-gut8-bg px-2 py-1 rounded-gut8 border border-border-gut8-light">
                  {dataSource.import_file}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Description */}
      {dataSource?.description && (
        <div className="px-6 py-3 bg-secondary-subtle border-b border-border-gut8-light -mx-6 mb-4">
          <p className="text-sm text-gut8-anthracite leading-relaxed">
            {dataSource.description}
          </p>
        </div>
      )}

      {/* Data Grid - 2 columns on desktop, 1 on mobile */}
      <div>
        {Object.keys(data).length === 0 ? (
          <p className="text-sm text-text-muted italic">Keine Daten verfügbar</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {Object.entries(data)
              .filter(([key, value]) => {
                // Filter out reference year fields (already shown in header as "Jahresausgabe")
                const lowerKey = key.toLowerCase()
                if (lowerKey === 'referenzjahr' || lowerKey === 'reference_year' || lowerKey === 'year') return false
                // Filter out fields marked as not visible in field labels
                if (!isFieldVisible(key)) return false
                // For hardcoded data (GV100): filter out null values
                // For dynamic provider data: show null values as "k.A."
                if (!showNullValues && (value === null || value === undefined)) return false
                return true
              })
              .map(([key, value]) => {
                const unit = getFieldUnit(key)
                const formattedValue = formatValue(key, value)
                // Use field label for display, falls back to formatted key if no label found
                const displayLabel = getDisplayLabel(key)

                return (
                  <div
                    key={key}
                    className="flex justify-between items-baseline py-2 border-b border-border-gut8-light hover:bg-primary-subtle transition-colors px-2 -mx-2 rounded-gut8"
                  >
                    <span className="text-sm text-text-muted mr-4">{displayLabel}</span>
                    <span className="text-sm font-semibold text-gut8-anthracite whitespace-nowrap">
                      {formattedValue}{unit && <span className="ml-1 text-text-muted font-normal">{unit}</span>}
                    </span>
                  </div>
                )
              })}
          </div>
        )}
      </div>
    </div>
  )
}
